// ════════════════════════════════════════════════════════════════════════
// env.ts  —  Punto único de lectura de las env vars Vite consumidas por la app
// ════════════════════════════════════════════════════════════════════════
//
// Antes de este módulo, `import.meta.env.VITE_API_URL` / `VITE_FILE_SERVER_URL` se leían
// duplicadas en ~15 archivos (`src/api`, `src/modules/**`, `src/pages`) con fallbacks
// inconsistentes entre sí (algunos `|| ''`, otros `|| 'http://localhost:5000'`). Se
// consolidan aquí con un único fallback por variable.
//
// Se exponen como FUNCIONES (no constantes de módulo) para poder testear con `vi.stubEnv`
// sin depender del orden de import — mismo patrón ya usado en `features.ts` (`isEnabled`) y
// documentado en `useOpportunityDocuments.ts`.
//
// `VITE_FILES_URL` (usada en `clients/opportunities`, spec.md C-8) sigue siendo una env var
// DISTINTA de `VITE_FILE_SERVER_URL`; se centraliza su lectura sin unificar sus valores.

import { z } from 'zod';

const DEFAULT_API_URL = '/api/v1';
const DEFAULT_FILE_SERVER_URL = 'http://localhost:5000';

const ConfigUrlSchema = z
  .string()
  .trim()
  .refine(
    (value) => {
      if (value.startsWith('/') && !value.startsWith('//')) return true;

      try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'debe ser una URL HTTP(S) o una ruta que empiece por /' }
  );

function readUrl(name: string, value: string | undefined, fallback: string): string {
  const parsed = ConfigUrlSchema.safeParse(value || fallback);
  if (!parsed.success) {
    throw new Error(`Variable de entorno ${name} inválida: ${parsed.error.issues[0].message}`);
  }

  return parsed.data === '/' ? '/' : parsed.data.replace(/\/+$/, '');
}

export function getApiUrl(): string {
  return readUrl('VITE_API_URL', import.meta.env.VITE_API_URL, DEFAULT_API_URL);
}

export function getFileServerUrl(): string {
  return readUrl(
    'VITE_FILE_SERVER_URL',
    import.meta.env.VITE_FILE_SERVER_URL,
    DEFAULT_FILE_SERVER_URL
  );
}

export function getOpportunityFilesUrl(): string {
  return readUrl('VITE_FILES_URL', import.meta.env.VITE_FILES_URL, DEFAULT_FILE_SERVER_URL);
}
